import _ from 'lodash'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

import Grid from '@mui/material/Unstable_Grid2'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'
import { useGetWaitingRoomQuery, useGetWaitingRoomDoctorsQuery } from '@/core/services/waiting-room'
import { skipToken } from '@reduxjs/toolkit/query'
import { WaitingRoom } from '@/core/models/waiting-room.model'
import { useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'

import WaitingRoomCard from '../components/WaitingRoomCard'

dayjs.extend(isBetween)

function FavoriteWaitingRoomItem({ waitingRoomId, navigateOnly }: { waitingRoomId: string; navigateOnly: boolean }) {
  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId)

  // navigateOnly cards don't render the doctors list — skip the status fetch too.
  const { statuses, isReady } = useRoomDoctorStatuses(navigateOnly ? undefined : waitingRoom?._id)

  const { doctors } = useGetWaitingRoomDoctorsQuery(navigateOnly ? skipToken : (waitingRoom?._id ?? skipToken), {
    selectFromResult: ({ data }) => ({
      doctors:
        data?.map((doctor) => ({
          id: doctor._id,
          status: statuses[doctor._id] ?? (isReady ? ('offline' as const) : undefined),
          name: `${doctor.userInfo.name} ${doctor.userInfo.surname}`,
        })) ?? [],
    }),
  })

  const isOpening = getIsOpening(waitingRoom)
  const specialities = waitingRoom?.specialities.map((speciality) => _.capitalize(speciality.name)) ?? []

  if (!waitingRoom) return null

  return (
    <Grid xs={12} md={6}>
      <WaitingRoomCard
        description={waitingRoom.description}
        doctors={doctors}
        isOpening={isOpening}
        name={waitingRoom.name}
        navigateOnly={navigateOnly}
        specialities={specialities}
        waitingRoomId={waitingRoom._id}
        waitingPatientsCount={waitingRoom.consultations.length}
        price={waitingRoom.price !== 0 ? waitingRoom.price : undefined}
      />
    </Grid>
  )
}

function getIsOpening(waitingRoom?: WaitingRoom): boolean {
  if (!waitingRoom) return false

  const openingHour = _.find(waitingRoom.openingHours, ({ day }) => day === dayjs().day())
  if (!openingHour) return false

  return _.some(openingHour.periods, (period) => dayjs().isBetween(dayjs(period.open, 'HH:mm'), dayjs(period.close, 'HH:mm')))
}

export default function FavoriteWaitingRoomsCard() {
  const authUser = useAppSelector(selectAuthUser)

  if (!isStaff(authUser)) return null

  const favoriteIds = authUser.favoriteWaitingRooms ?? []
  const navigateOnly = authUser.status !== 'PDS'

  if (favoriteIds.length === 0) return null

  return (
    <Grid container spacing={3}>
      {favoriteIds.map((id) => (
        <FavoriteWaitingRoomItem key={id} waitingRoomId={id} navigateOnly={navigateOnly} />
      ))}
    </Grid>
  )
}
